/**
 * Notification Hook
 *
 * This hook provides utilities for displaying various types of notifications
 * with enhanced functionality like embedded links.
 *
 * Features:
 * - Toast notifications with clickable links
 * - Internationalization support
 * - Custom notification display options
 *
 * @module hooks/useNotification
 */

import { useTranslation, Trans } from "react-i18next";

/**
 * Hook for managing and displaying notifications
 *
 * @returns {Object} Notification utility functions
 * @returns {Function} returns.showToastWithLink - Function to show toast with embedded link
 */
export const useNotification = () => {
  const { t } = useTranslation();

  /**
   * Display a toast notification with an embedded clickable link
   *
   * @param {Object} options - Toast configuration options
   * @param {string} options.url - URL to be linked
   * @param {string} options.msg - Translation key for the message
   * @param {Function} options.showToast - Toast function to call (from react-toastify)
   * @returns {void}
   */
  const showToastWithLink = ({ url, msg, showToast }) => {
    // Create message with translated text and embedded link
    const message = (
      <>
        <Trans i18nKey={msg} />
        <br />
        <a href={url} target="_blank" rel="noreferrer">
          <Trans i18nKey={t("viewHere")} />
        </a>
      </>
    );
    // Display the toast with the link, disable auto-close
    showToast(message, { autoClose: false });
  };

  // Return hook utilities
  return { showToastWithLink };
};
